import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, PauseCircle, Play, RotateCcw, Workflow } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { formatDateTime } from "../lib/assist";
import {
  EFFECT_KIND_LABELS, STATUS_LABELS, groupRulesByModule, countsLine, parametersErrors, ruleState, runSummary, statusTone,
} from "../lib/automation";
import { LoadError, SaveBar, SectionHeader } from "./Settings";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const TONES = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  bad: "bg-red-50 text-red-700 border-red-200",
  warn: "bg-amber-50 text-amber-800 border-amber-200",
  muted: "bg-slate-100 text-slate-600 border-slate-200",
};
const PAGE = 30;

function Pill({ tone, children, testid }) {
  return <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${TONES[tone]}`}>{children}</span>;
}

function RuleCard({ rule, engineEnabled, canManage, busy, onToggle, onResume, onShowRuns }) {
  const state = ruleState(rule, engineEnabled);
  return (
    <li className="px-4 py-4 sm:px-5 flex flex-col gap-3 sm:flex-row sm:items-start" data-testid={`rule-${rule.id}`}>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="font-semibold text-sm text-slate-900">{rule.label}</h4>
          <Pill tone={state.tone} testid={`rule-state-${rule.id}`}>{state.label}</Pill>
        </div>
        <p className="text-sm text-slate-600">{rule.description}</p>
        <p className="text-xs text-slate-500">
          Déclencheur : {rule.event_label} · {EFFECT_KIND_LABELS[rule.effect_kind]}
        </p>
        <p className="text-xs text-slate-500" data-testid={`rule-counts-${rule.id}`}>{countsLine(rule.counts_7d)}</p>
        {rule.paused && (
          <p className="text-xs text-red-700 flex items-center gap-1.5" data-testid={`rule-paused-${rule.id}`}>
            <PauseCircle size={14} aria-hidden /> Mise en pause : {rule.paused_reason}. Ses événements attendent.
          </p>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 pt-0.5">
          <button type="button" className="text-xs font-medium text-jamora hover:underline min-h-[28px] [@media(pointer:coarse)]:min-h-[44px]"
            onClick={() => onShowRuns(rule.id)} data-testid={`btn-rule-runs-${rule.id}`}>Voir les exécutions</button>
          {rule.paused && canManage && (
            <button type="button" className="text-xs font-medium text-jamora hover:underline min-h-[28px] [@media(pointer:coarse)]:min-h-[44px]"
              onClick={() => onResume(rule.id)} disabled={busy} data-testid={`btn-rule-resume-${rule.id}`}>Reprendre</button>
          )}
        </div>
      </div>
      <label className="flex items-center gap-2 sm:flex-col sm:items-end sm:gap-1 shrink-0 text-xs text-slate-500">
        <Switch checked={rule.enabled} disabled={!canManage || busy || !rule.module_on} onCheckedChange={(on) => onToggle(rule, on)}
          aria-label={`${rule.enabled ? "Éteindre" : "Activer"} la règle « ${rule.label} »`} data-testid={`switch-rule-${rule.id}`} />
        <span>{rule.enabled ? "Activée" : "Éteinte"}</span>
      </label>
    </li>
  );
}

function RunsTable({ runs, loading, canManage, replaying, onReplay }) {
  if (loading && runs.length === 0) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-jamora" /></div>;
  if (runs.length === 0) {
    return (
      <div className="px-5 py-10 text-center text-sm text-slate-500" data-testid="runs-empty">
        <p className="font-medium text-slate-700">Aucune exécution pour ce filtre</p>
        <p className="mt-1">Une règle n'apparaît ici qu'une fois qu'elle a réagi à un fait.</p>
      </div>
    );
  }
  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="runs-table">
          <thead>
            <tr className="text-left text-xs font-semibold text-slate-500 border-b border-slate-100">
              <th className="px-5 py-2.5">Quand</th><th className="px-3 py-2.5">Règle</th><th className="px-3 py-2.5">État</th><th className="px-3 py-2.5">Détail</th><th className="px-5 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-b border-slate-50 last:border-0 h-12" data-testid={`run-${r.id}`}>
                <td className="px-5 whitespace-nowrap text-slate-600">{formatDateTime(r.started_at)}</td>
                <td className="px-3 text-slate-900">{r.rule_label}</td>
                <td className="px-3"><Pill tone={statusTone(r.status)}>{STATUS_LABELS[r.status] || r.status}</Pill></td>
                <td className="px-3 text-slate-600">{runSummary(r)}</td>
                <td className="px-5 text-right">
                  {r.status === "failed" && canManage && (
                    <Button variant="outline" size="sm" disabled={replaying === r.id} onClick={() => onReplay(r.id)} data-testid={`btn-replay-${r.id}`}>
                      <RotateCcw size={14} className="mr-1.5" aria-hidden /> Rejouer
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="md:hidden divide-y divide-slate-100" data-testid="runs-list">
        {runs.map((r) => (
          <li key={r.id} className="px-4 py-3 space-y-1.5" data-testid={`run-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <span className="text-sm font-medium text-slate-900">{r.rule_label}</span>
              <Pill tone={statusTone(r.status)}>{STATUS_LABELS[r.status] || r.status}</Pill>
            </div>
            <p className="text-xs text-slate-500">{formatDateTime(r.started_at)}</p>
            <p className="text-sm text-slate-600">{runSummary(r)}</p>
            {r.status === "failed" && canManage && (
              <Button variant="outline" size="sm" className="w-full min-h-[44px]" disabled={replaying === r.id} onClick={() => onReplay(r.id)} data-testid={`btn-replay-${r.id}`}>
                <RotateCcw size={14} className="mr-1.5" aria-hidden /> Rejouer
              </Button>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

// Paramètres › Automatisations (Ordre 25). automation:view reads; automation:manage switches rules, resumes, replays and sets the delays;
// the emergency stop of the whole engine is the administrator's. Nothing here can make a rule do more than its catalogue line says.
export default function SettingsAutomation() {
  const { user } = useAuth();
  const canManage = hasPermission(user, "automation:manage");
  const [overview, setOverview] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [runs, setRuns] = useState([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsFailed, setRunsFailed] = useState(false);
  const [filter, setFilter] = useState({ rule: "all", status: "all" });
  const [replaying, setReplaying] = useState(null);
  const [confirmStop, setConfirmStop] = useState(false);
  const [saved, setSaved] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const applyParameters = (parameters) => {
    setSaved(parameters);
    setValues(Object.fromEntries(Object.entries(parameters).map(([k, v]) => [k, String(v)])));
  };
  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      const { data } = await api.get("/automation/overview");
      setOverview(data);
      setSaved((previous) => {
        if (previous == null) setValues(Object.fromEntries(Object.entries(data.parameters).map(([k, v]) => [k, String(v)])));
        return previous ?? data.parameters;
      });
    } catch (e) {
      setLoadFailed(true);
    }
  }, []);
  const loadRuns = useCallback(async (skip = 0) => {
    setRunsLoading(true);
    setRunsFailed(false);
    try {
      const params = { skip, limit: PAGE, days: 30 };
      if (filter.rule !== "all") params.rule = filter.rule;
      if (filter.status !== "all") params.status = filter.status;
      const { data } = await api.get("/automation/runs", { params });
      setRuns((previous) => (skip ? [...previous, ...data.items] : data.items));
      setRunsTotal(data.total);
    } catch (e) {
      setRunsFailed(true);
    } finally {
      setRunsLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRuns(0); }, [loadRuns]);

  const act = async (call, success) => {
    setBusy(true);
    try {
      await call();
      if (success) toast.success(success);
      await Promise.all([load(), loadRuns(0)]);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const toggle = (rule, on) => act(() => api.put(`/automation/rules/${rule.id}`, { enabled: on }), on ? "Règle activée : elle ne s'applique qu'aux faits à venir" : "Règle éteinte");
  const resume = (id) => act(() => api.post(`/automation/rules/${id}/resume`), "Règle reprise");
  const switchEngine = (on) => act(() => api.put("/automation/engine", { enabled: on }), on ? "Moteur remis en marche" : "Moteur arrêté : les faits attendent");
  const replay = async (id) => {
    setReplaying(id);
    await act(() => api.post(`/automation/runs/${id}/replay`), "Exécution relancée");
    setReplaying(null);
  };

  const errors = parametersErrors(values, overview?.parameters_meta);
  const dirty = saved != null && Object.keys(saved).some((k) => String(saved[k]) !== values[k]);
  const save = async () => {
    setSaving(true);
    try {
      const changed = Object.fromEntries(Object.keys(saved).filter((k) => String(saved[k]) !== values[k]).map((k) => [k, Number(values[k])]));
      const { data } = await api.put("/automation/parameters", changed);
      applyParameters(data);
      toast.success("Réglages enregistrés");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loadFailed) return <LoadError onRetry={load} />;
  if (overview == null) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-jamora" /></div>;

  const engineOn = overview.engine_enabled;
  return (
    <div className="space-y-4" data-testid="settings-automation">
      <SectionHeader title="Automatisations" description="Ce que le CRM fait seul quand un fait se produit. Chaque règle est éteinte tant que vous ne l'activez pas." />
      <section className="bg-white border border-slate-200 rounded-xl p-4 sm:px-5 flex flex-col gap-3 md:flex-row md:items-center md:gap-6" data-testid="engine-card">
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            {engineOn ? <CheckCircle2 size={18} className="text-emerald-600" aria-hidden /> : <AlertTriangle size={18} className="text-amber-600" aria-hidden />}
            <h4 className="font-semibold text-sm text-slate-900" data-testid="engine-state">{engineOn ? "Moteur en marche" : "Moteur arrêté"}</h4>
          </div>
          <p className="text-sm text-slate-600">
            {engineOn
              ? "Les règles activées s'appliquent aux faits qui se produisent."
              : "Aucune règle ne s'applique. Les faits qui se produisent attendent et seront traités à la remise en marche."}
          </p>
          {!overview.can_stop_engine && <p className="text-xs text-slate-500">L'arrêt d'urgence est réservé à l'administrateur.</p>}
        </div>
        <dl className="text-xs text-slate-500 grid grid-cols-2 gap-4 md:flex md:gap-6">
          <div><dt>Faits en attente</dt><dd className="text-base font-semibold text-slate-900" data-testid="waiting-events">{overview.waiting_events}</dd></div>
          <div><dt>Échecs à examiner</dt><dd className="text-base font-semibold text-slate-900" data-testid="failed-runs">{overview.failed_runs}</dd></div>
        </dl>
        {overview.can_stop_engine && (
          engineOn ? (
            <Button variant="outline" className="md:w-auto w-full text-red-700 border-red-200 hover:bg-red-50" onClick={() => setConfirmStop(true)} disabled={busy} data-testid="btn-engine-stop">
              Arrêter le moteur
            </Button>
          ) : (
            <Button className="md:w-auto w-full" onClick={() => switchEngine(true)} disabled={busy} data-testid="btn-engine-start">
              <Play size={14} className="mr-1.5" aria-hidden /> Remettre en marche
            </Button>
          )
        )}
      </section>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] items-start">
        <div className="space-y-4 min-w-0">
          <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div data-testid="rules-list">
              {groupRulesByModule(overview.rules).map((group) => (
                <div key={group.module} data-testid={`rules-group-${group.module}`}>
                  <h4 className="px-4 py-2 sm:px-5 bg-slate-50 border-y border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">{group.label}</h4>
                  <ul className="divide-y divide-slate-100">
                    {group.rules.map((rule) => (
                      <RuleCard key={rule.id} rule={rule} engineEnabled={engineOn} canManage={canManage} busy={busy}
                        onToggle={toggle} onResume={resume} onShowRuns={(id) => { setFilter({ rule: id, status: "all" }); document.getElementById("automation-runs")?.scrollIntoView({ behavior: "smooth" }); }} />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-4 min-w-0">
          <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="parameters-card">
            <h4 className="font-semibold text-sm text-slate-900">Réglages</h4>
            {(overview.parameters_meta || []).map(({ name: key, label, unit }) => (
              <div key={key} className="space-y-1">
                <label htmlFor={`param-${key}`} className="text-xs font-medium text-slate-700">{label}</label>
                <div className="flex items-center gap-2">
                  <Input id={`param-${key}`} type="number" min={1} className="w-24" value={values[key] ?? ""} disabled={!canManage}
                    onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))} aria-invalid={!!errors[key]} data-testid={`param-${key}`} />
                  <span className="text-sm text-slate-500">{unit}</span>
                </div>
                {errors[key] && <p className="text-xs text-red-600">{errors[key]}</p>}
              </div>
            ))}
          </section>

          <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-2" data-testid="already-delivered">
            <h4 className="font-semibold text-sm text-slate-900 flex items-center gap-2"><Workflow size={16} aria-hidden /> Déjà assuré ailleurs</h4>
            <p className="text-xs text-slate-500">Ces alertes existent déjà dans leur module : les répéter ici préviendrait deux fois.</p>
            <ul className="text-sm text-slate-600 space-y-1.5">
              {overview.already_delivered.map((d) => (
                <li key={d.label}><span className="text-slate-900">{d.label}</span><span className="block text-xs text-slate-500">{d.where}</span></li>
              ))}
            </ul>
          </section>
        </aside>
      </div>

        <section id="automation-runs" className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="runs-section">
          <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h4 className="font-semibold text-sm text-slate-900">Exécutions des 30 derniers jours</h4>
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <Select value={filter.rule} onValueChange={(rule) => setFilter((f) => ({ ...f, rule }))}>
                <SelectTrigger className="sm:w-56" aria-label="Filtrer par règle" data-testid="filter-rule"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les règles</SelectItem>
                  {overview.rules.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filter.status} onValueChange={(status) => setFilter((f) => ({ ...f, status }))}>
                <SelectTrigger className="sm:w-44" aria-label="Filtrer par état" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les états</SelectItem>
                  {["done", "skipped", "retry", "failed"].map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {runsFailed ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500 space-y-3" data-testid="runs-error">
              <p>Impossible de charger les exécutions.</p>
              <Button variant="outline" size="sm" onClick={() => loadRuns(0)}>Réessayer</Button>
            </div>
          ) : (
            <RunsTable runs={runs} loading={runsLoading} canManage={canManage} replaying={replaying} onReplay={replay} />
          )}
          {runs.length > 0 && runs.length < runsTotal && (
            <div className="px-5 py-3 border-t border-slate-100 text-center">
              <Button variant="ghost" size="sm" disabled={runsLoading} onClick={() => loadRuns(runs.length)} data-testid="btn-runs-more">
                Voir plus ({runsTotal - runs.length})
              </Button>
            </div>
          )}
        </section>

      <SaveBar dirty={dirty} saveDisabled={Object.keys(errors).length > 0} saving={saving} canManage={canManage} onSave={save} onRevert={() => applyParameters(saved)} />

      <AlertDialog open={confirmStop} onOpenChange={setConfirmStop}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Arrêter le moteur d'automatisation ?</AlertDialogTitle>
            <AlertDialogDescription>
              Plus aucune règle ne s'appliquera. Rien n'est perdu : les faits qui se produisent attendent et seront traités quand vous remettrez le moteur en marche.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => switchEngine(false)} data-testid="btn-engine-stop-confirm">Arrêter</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
